import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Plus, Trash2, User } from 'lucide-react';
import { groomers as groomersApi, services as servicesApi, groomerCapacity as capacityApi } from '../utils/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const GroomerCapacityManager = () => {
  const [groomers, setGroomers] = useState([]);
  const [services, setServices] = useState([]);
  const [sizeCategories, setSizeCategories] = useState([]);
  const [sizingEnabled, setSizingEnabled] = useState(true);
  const [servicesEnabled, setServicesEnabled] = useState(true);
  const [selectedGroomer, setSelectedGroomer] = useState(null);
  const [defaults, setDefaults] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('defaults');
  
  // Form states
  const [newDefault, setNewDefault] = useState({
    size: '',
    serviceId: '',
    maxPerDay: ''
  });
  
  const [newOverride, setNewOverride] = useState({
    specificDate: '',
    size: '',
    serviceId: '',
    maxPerDay: ''
  });

  useEffect(() => {
    loadInitialData();
    
    // Listen for size category and service updates
    const handleUpdate = () => {
      console.log('[GroomerCapacityManager] Received update event, reloading data');
      loadInitialData();
    };
    
    window.addEventListener('sizeCategoryUpdated', handleUpdate);
    window.addEventListener('serviceUpdated', handleUpdate);
    
    return () => {
      window.removeEventListener('sizeCategoryUpdated', handleUpdate);
      window.removeEventListener('serviceUpdated', handleUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedGroomer) {
      loadGroomerCapacity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroomer]);

  const loadInitialData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const [groomersRes, servicesRes, sizeCategoriesRes, sizingSettingsRes, servicesEnabledRes] = await Promise.all([
        groomersApi.getAll(),
        servicesApi.getAll(),
        axios.get(`${API_URL}/size-categories`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/size-categories/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/business-info/services_enabled`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { info_value: 'true' } })),
      ]);
      setGroomers(groomersRes.data);
      setServices(servicesRes.data);
      setSizeCategories(sizeCategoriesRes.data);
      setSizingEnabled(sizingSettingsRes.data.sizing_enabled === 'true');
      setServicesEnabled(servicesEnabledRes.data.info_value !== 'false');
      if (groomersRes.data.length > 0) {
        setSelectedGroomer(groomersRes.data[0].id);
      }
    } catch (error) {
      console.warn('Employee capacity data not loaded yet');
    } finally {
      setLoading(false);
    }
  };

  const loadGroomerCapacity = async () => {
    if (!selectedGroomer) return;
    
    try {
      const [defaultsRes, overridesRes] = await Promise.all([
        capacityApi.getDefaults(selectedGroomer),
        capacityApi.getOverrides(selectedGroomer, {})
      ]);
      setDefaults(defaultsRes.data);
      setOverrides(overridesRes.data);
    } catch (error) {
      console.warn('Employee capacity settings not loaded');
    }
  };

  const handleAddDefault = async () => {
    // When services/sizing are disabled, only maxPerDay is required
    // Otherwise, require at least size or serviceId
    if (!newDefault.maxPerDay) {
      toast.error('Please enter the max appointments per day');
      return;
    }
    
    if (sizingEnabled && servicesEnabled && !newDefault.size && !newDefault.serviceId) {
      toast.error('Please select a size or service');
      return;
    }

    try {
      await capacityApi.setDefault({
        groomerId: selectedGroomer,
        size: newDefault.size || null,
        serviceId: newDefault.serviceId || null,
        maxPerDay: parseInt(newDefault.maxPerDay)
      });
      
      toast.success('Default capacity added');
      setNewDefault({ size: '', serviceId: '', maxPerDay: '' });
      loadGroomerCapacity();
    } catch (error) {
      toast.error('Failed to add default capacity');
    }
  };

  const handleDeleteDefault = async (id) => {
    if (!window.confirm('Delete this capacity limit?')) return;
    
    try {
      await capacityApi.deleteDefault(id);
      toast.success('Capacity limit deleted');
      loadGroomerCapacity();
    } catch (error) {
      toast.error('Failed to delete capacity limit');
    }
  };

  const handleAddOverride = async () => {
    if (!newOverride.specificDate || !newOverride.maxPerDay || (!newOverride.size && !newOverride.serviceId)) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await capacityApi.setOverride({
        groomerId: selectedGroomer,
        specificDate: newOverride.specificDate,
        size: newOverride.size || null,
        serviceId: newOverride.serviceId || null,
        maxPerDay: parseInt(newOverride.maxPerDay)
      });
      
      toast.success('Date override added');
      setNewOverride({ specificDate: '', size: '', serviceId: '', maxPerDay: '' });
      loadGroomerCapacity();
    } catch (error) {
      toast.error('Failed to add date override');
    }
  };

  const handleDeleteOverride = async (id) => {
    if (!window.confirm('Delete this override?')) return;
    
    try {
      await capacityApi.deleteOverride(id);
      toast.success('Override deleted');
      loadGroomerCapacity();
    } catch (error) {
      toast.error('Failed to delete override');
    }
  };

  const getSizeName = (size) => {
    if (!size) return 'All Sizes';
    const category = sizeCategories.find(c => c.category_label.toLowerCase().replace(/\s*\([^)]*\)/g, '').trim().replace(/\s+/g, '_') === size);
    return category ? category.category_label : size.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getServiceName = (serviceId) => {
    if (!serviceId) return 'All Services';
    const service = services.find(s => s.id === serviceId);
    return service ? service.name : 'Unknown Service';
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  // Simplified mode when services/sizing are disabled
  if (!sizingEnabled && !servicesEnabled) {
    return (
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <User className="text-primary-600" size={24} />
          <h2 className="text-2xl font-bold">Employee Capacity Management</h2>
        </div>
        
        <p className="text-gray-600 mb-6">
          Set the maximum number of appointments each employee can handle per day.
        </p>

        {/* Groomer Selection */}
        <div className="mb-6">
          <label className="label">Select Employee</label>
          <select
            value={selectedGroomer || ''}
            onChange={(e) => setSelectedGroomer(e.target.value)}
            className="input"
          >
            {groomers.map(groomer => (
              <option key={groomer.id} value={groomer.id}>
                {groomer.first_name} {groomer.last_name}
              </option>
            ))}
          </select>
        </div>

        {selectedGroomer && (
          <div className="space-y-4">
            {/* Show existing default capacity for this groomer (no size/service) */}
            {defaults.filter(d => !d.size && !d.service_id).length > 0 ? (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3">Current Daily Limit</h3>
                {defaults.filter(d => !d.size && !d.service_id).map(d => (
                  <div key={d.id} className="flex items-center justify-between">
                    <span className="text-lg font-medium">{d.max_per_day} appointments per day</span>
                    <button
                      onClick={() => handleDeleteDefault(d.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm">
                  No daily limit set for this employee. They can accept unlimited appointments.
                </p>
              </div>
            )}

            {/* Add/Update capacity */}
            <div className="bg-white border rounded-lg p-4">
              <h3 className="font-semibold mb-3">Set Daily Limit</h3>
              <div className="flex items-end space-x-4">
                <div className="flex-1">
                  <label className="label">Max Appointments Per Day</label>
                  <input
                    type="number"
                    min="1"
                    value={newDefault.maxPerDay}
                    onChange={(e) => setNewDefault({ ...newDefault, maxPerDay: e.target.value, size: '', serviceId: '' })}
                    placeholder="e.g., 8"
                    className="input"
                  />
                </div>
                <button
                  onClick={handleAddDefault}
                  className="btn btn-primary"
                >
                  <Plus size={16} className="mr-1" />
                  Set Limit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-6">
        <User className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Employee Capacity Management</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Set individual capacity limits for each employee by size and/or service type. 
        You can also set date-specific overrides for special circumstances.
      </p>

      {/* Groomer Selection */}
      <div className="mb-6">
        <label className="label">Select Employee</label>
        <select
          value={selectedGroomer || ''}
          onChange={(e) => setSelectedGroomer(e.target.value)}
          className="input"
        >
          {groomers.map(groomer => (
            <option key={groomer.id} value={groomer.id}>
              {groomer.first_name} {groomer.last_name}
            </option>
          ))}
        </select>
      </div>

      {selectedGroomer && (
        <>
          {/* Tabs */}
          <div className="flex space-x-2 mb-6 border-b">
            <button
              onClick={() => setActiveTab('defaults')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'defaults'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Default Limits
            </button>
            <button
              onClick={() => setActiveTab('overrides')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'overrides'
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Date Overrides
            </button>
          </div>

          {/* Default Limits Tab */}
          {activeTab === 'defaults' && (
            <div className="space-y-6">
              {/* Add New Default */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-4">Add Default Capacity Limit</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="label">Size (Optional)</label>
                    <select
                      value={newDefault.size}
                      onChange={(e) => setNewDefault({ ...newDefault, size: e.target.value })}
                      className="input"
                    >
                      <option value="">All Sizes</option>
                      {sizeCategories
                        .filter(cat => cat.is_active)
                        .sort((a, b) => a.display_order - b.display_order)
                        .map(cat => {
                          const cleanLabel = cat.category_label
                            .replace(/\s*\([^)]*\)/g, '')
                            .trim()
                            .toLowerCase()
                            .replace(/\s+/g, '_');
                          return (
                            <option key={cat.id} value={cleanLabel}>
                              {cat.category_label}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                  <div>
                    <label className="label">Service (Optional)</label>
                    <select
                      value={newDefault.serviceId}
                      onChange={(e) => setNewDefault({ ...newDefault, serviceId: e.target.value })}
                      className="input"
                    >
                      <option value="">All Services</option>
                      {services.map(service => (
                        <option key={service.id} value={service.id}>{service.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Max Per Day *</label>
                    <input
                      type="number"
                      min="0"
                      value={newDefault.maxPerDay}
                      onChange={(e) => setNewDefault({ ...newDefault, maxPerDay: e.target.value })}
                      className="input"
                      placeholder="e.g., 5"
                    />
                  </div>
                  <div className="flex items-end">
                    <button onClick={handleAddDefault} className="btn btn-primary w-full flex items-center justify-center">
                      <Plus size={18} className="mr-1" />
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Existing Defaults */}
              <div>
                <h3 className="font-semibold mb-3">Current Default Limits</h3>
                {defaults.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No default limits set for this employee</p>
                ) : (
                  <div className="space-y-2">
                    {defaults.map(def => (
                      <div key={def.id} className="flex items-center justify-between p-4 bg-white border rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium">
                            {getSizeName(def.size)} • {getServiceName(def.service_id)}
                          </div>
                          <div className="text-sm text-gray-600">
                            Maximum: {def.max_per_day} per day
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteDefault(def.id)}
                          className="text-red-600 hover:text-red-800 p-2"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Date Overrides Tab */}
          {activeTab === 'overrides' && (
            <div className="space-y-6">
              {/* Add New Override */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-4">Add Date-Specific Override</h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <label className="label">Date *</label>
                    <input
                      type="date"
                      value={newOverride.specificDate}
                      onChange={(e) => setNewOverride({ ...newOverride, specificDate: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Size (Optional)</label>
                    <select
                      value={newOverride.size}
                      onChange={(e) => setNewOverride({ ...newOverride, size: e.target.value })}
                      className="input"
                    >
                      <option value="">All Sizes</option>
                      {sizeCategories
                        .filter(cat => cat.is_active)
                        .sort((a, b) => a.display_order - b.display_order)
                        .map(cat => {
                          const cleanLabel = cat.category_label
                            .replace(/\s*\([^)]*\)/g, '')
                            .trim()
                            .toLowerCase()
                            .replace(/\s+/g, '_');
                          return (
                            <option key={cat.id} value={cleanLabel}>
                              {cat.category_label}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                  <div>
                    <label className="label">Service (Optional)</label>
                    <select
                      value={newOverride.serviceId}
                      onChange={(e) => setNewOverride({ ...newOverride, serviceId: e.target.value })}
                      className="input"
                    >
                      <option value="">All Services</option>
                      {services.map(service => (
                        <option key={service.id} value={service.id}>{service.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Max Per Day *</label>
                    <input
                      type="number"
                      min="0"
                      value={newOverride.maxPerDay}
                      onChange={(e) => setNewOverride({ ...newOverride, maxPerDay: e.target.value })}
                      className="input"
                      placeholder="e.g., 3"
                    />
                  </div>
                  <div className="flex items-end">
                    <button onClick={handleAddOverride} className="btn btn-primary w-full flex items-center justify-center">
                      <Plus size={18} className="mr-1" />
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Existing Overrides */}
              <div>
                <h3 className="font-semibold mb-3">Current Date Overrides</h3>
                {overrides.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No date overrides set for this employee</p>
                ) : (
                  <div className="space-y-2">
                    {overrides.map(override => (
                      <div key={override.id} className="flex items-center justify-between p-4 bg-white border rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium">
                            {new Date(override.specific_date).toLocaleDateString()} • {getSizeName(override.size)} • {getServiceName(override.service_id)}
                          </div>
                          <div className="text-sm text-gray-600">
                            Maximum: {override.max_per_day} per day
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteOverride(override.id)}
                          className="text-red-600 hover:text-red-800 p-2"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">💡 How it works:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>Default Limits:</strong> Apply to all days unless overridden</li>
              <li>• <strong>Date Overrides:</strong> Take priority over defaults for specific dates</li>
              <li>• Leave size or service blank to apply to all sizes/services</li>
              <li>• More specific rules (size + service) take priority over general rules</li>
              <li>• Set to 0 to prevent bookings for that combination</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default GroomerCapacityManager;
